import { ENDPOINTS, getFullUrl } from "./config";

/**
 * Catalogue APIs for the admin products section: the V3 product endpoints plus
 * the masters they reference (classification, domains, salts, drugs, schedules
 * and product forms).
 *
 * Every call goes through `request`, which returns the parsed body as-is so
 * callers keep reading `success`, `message`, `data` and the pagination fields
 * exactly as the service sends them. Network and parse failures come back in
 * the same envelope rather than throwing, matching InverntroyService.
 */

export type ClassificationType = "MEDICINE" | "NON_MEDICINE";

export interface ApiEnvelope<T = any> {
  success: boolean;
  message?: string;
  data: T;
  totalItems?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export interface MasterOption {
  id: string;
  name: string;
  icon?: string | null;
}

export interface MasterRow {
  id: string;
  name: string;
  classificationType?: string;
  categoryIcon?: string | null;
  sortOrder?: number;
  isVisible?: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface HierarchyRow {
  id: string;
  classificationType: string;
  primaryCategoryId: string;
  primaryCategoryName: string | null;
  subcategoryId: string;
  subcategoryName: string | null;
  groupId: string;
  groupName: string | null;
  isActive: boolean;
}

export interface DomainRuleRow {
  id: string;
  primaryCategoryId: string;
  primaryCategoryName: string | null;
  subcategoryId?: string;
  subcategoryName?: string | null;
  domainId: string;
  domainName: string | null;
  regulatoryDomainId: string;
  regulatoryDomainName: string | null;
  isAllowed: boolean;
}

export interface SaltDrugMappingRow {
  id: string;
  saltId: string;
  saltName: string | null;
  drugId: string;
  drugName: string | null;
}

export interface ScheduleRow {
  id: number;
  name: string;
  description: string | null;
  isActive: boolean;
}

export interface ProductFormRow {
  id: string;
  name: string;
  formGroupId: string;
  formGroupName?: string | null;
  isActive: boolean;
}

export interface AdminProductRow {
  productId: string;
  productName: string;
  description: string | null;
  isActive: boolean | null;
  isPublished: boolean;
  domainName: string | null;
}

/**
 * One master reference the enrichment agent proposed, resolved by the service
 * against the real masters.
 *
 * `id` is set only when the name matched an existing row. `isNew` means it has
 * to be created before a product can reference it, and `nearestExisting` warns
 * that a similarly named master is already there - accepting the new name would
 * split the taxonomy.
 */
export interface ResolvedRef {
  id: string | null;
  name: string | null;
  isNew: boolean;
  nearestExisting?: { id: string; name: string; similarity: number };
}

export interface ResolvedSalt extends ResolvedRef {
  strengthValue: number | null;
  strengthUnit: string | null;
}

export interface EnrichedProductInfo {
  introduction: string | null;
  howToUse: string | null;
  safetyAdvise: string | null;
  ifMiss: string | null;
  factBox: string | null;
  primaryUse: string | null;
  storage: string | null;
  commonSideEffect: string | null;
  alcoholInteraction: string | null;
  pregnancyInteraction: string | null;
  lactationInteraction: string | null;
  drivingInteraction: string | null;
  kidneyInteraction: string | null;
  liverInteraction: string | null;
  qAndA: string | null;
  howItWorks: string | null;
  interaction: string | null;
  keyIngredients: string | null;
  keyBenefits: string | null;
  productHighlights: string | null;
  /** Provenance stamped by the service, e.g. "AI_GENERATED:gpt-4o-mini". */
  source: string;
  /** Always true: the content is drafted, not verified. */
  reviewRequired: boolean;
}

/** Mirrors IEnrichedProductResponse in the inventory service. */
export interface EnrichedProduct {
  input: {
    productName: string;
    brandName?: string | null;
    marketerName?: string | null;
    classificationType?: string | null;
    additionalContext?: string | null;
  };
  classificationType: string | null;
  flags: {
    isMedicine: boolean;
    isRxRequired: boolean;
    isReturnable: boolean;
    coldChainRequired: boolean;
    discretePackaging: boolean;
  };
  primaryCategory: ResolvedRef;
  subCategory: ResolvedRef;
  productGroup: ResolvedRef;
  productFormGroup: ResolvedRef;
  productForm: ResolvedRef;
  domain: ResolvedRef;
  regulatoryDomain: ResolvedRef;
  marketer: ResolvedRef;
  categoryHierarchy: ResolvedRef;
  salts: ResolvedSalt[];
  compositionSource:
    | "CATALOGUE"
    | "MODEL_GROUNDED"
    | "MODEL_UNGROUNDED"
    | "NONE";
  matchedCatalogueProduct: {
    id: string;
    name: string;
    similarity: number;
  } | null;
  packaging: {
    packUnit: string | null;
    packQuantity: number | null;
    baseUnit: string | null;
    multipackCount: number | null;
    packagingText: string | null;
    unmatchedValues: string[];
  };
  medicineAttributes: {
    route: string | null;
    releaseType: string | null;
    disintegrationType: string | null;
    saltCompositionText: string | null;
    unmatchedValues: string[];
  } | null;
  productAttributes: {
    variant: string | null;
    flavour: string | null;
    targetGroup: string | null;
    dietType: string | null;
    sugarStatus: string | null;
    keyIngredients: string | null;
  };
  productInfo: EnrichedProductInfo | null;
  hasNewMasters: boolean;
  newMasters: string[];
  confidence: string | null;
  notes: string | null;
  toolRounds: number;
  toolsUsed: string[];
  model: string;
}

type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export class ProductCatalogService {
  private static getToken(): string | null {
    return localStorage.getItem("token");
  }

  /** Drops empty params so an unset filter is never sent as "undefined". */
  private static buildUrl(endpoint: string, params?: QueryParams): string {
    const url = new URL(getFullUrl(endpoint));
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      url.searchParams.append(key, String(value));
    });
    return url.toString();
  }

  private static async request<T = any>(
    endpoint: string,
    options: {
      method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
      params?: QueryParams;
      body?: unknown;
      fallbackData?: T;
      errorMessage?: string;
    } = {}
  ): Promise<ApiEnvelope<T>> {
    const {
      method = "GET",
      params,
      body,
      fallbackData,
      errorMessage = "Request failed",
    } = options;
    try {
      const response = await fetch(this.buildUrl(endpoint, params), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : errorMessage,
        data: fallbackData as T,
      };
    }
  }

  // =========================================================================
  // Classification masters
  // =========================================================================

  static getPrimaryCategories(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
    isVisible?: boolean;
  }) {
    return this.request<MasterRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.PRIMARY_CATEGORY_LIST,
      { params, fallbackData: [] as MasterRow[] }
    );
  }

  static getPrimaryCategoryOptions(params: {
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
    isVisible?: boolean;
  }) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.PRIMARY_CATEGORY_OPTIONS,
      { params, fallbackData: [] as MasterOption[] }
    );
  }

  static createPrimaryCategory(body: {
    name: string;
    classificationType: ClassificationType;
    categoryIcon?: string | null;
    sortOrder?: number;
    isVisible?: boolean;
    isActive?: boolean;
  }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.PRIMARY_CATEGORY_CREATE, {
      method: "POST",
      body,
    });
  }

  static updatePrimaryCategory(
    id: string,
    body: {
      classificationType?: ClassificationType;
      categoryIcon?: string | null;
      sortOrder?: number;
      isVisible?: boolean;
      isActive?: boolean;
    }
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.PRIMARY_CATEGORY_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getSubcategories(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.SUBCATEGORY_LIST,
      { params, fallbackData: [] as MasterRow[] }
    );
  }

  static getSubcategoryOptions(params: {
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
  }) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.SUBCATEGORY_OPTIONS,
      { params, fallbackData: [] as MasterOption[] }
    );
  }

  static createSubcategory(body: {
    name: string;
    classificationType: ClassificationType;
    isActive?: boolean;
  }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.SUBCATEGORY_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateSubcategory(
    id: string,
    body: { classificationType?: ClassificationType; isActive?: boolean }
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.SUBCATEGORY_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getGroups(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(ENDPOINTS.PRODUCT_CATEGORY.GROUP_LIST, {
      params,
      fallbackData: [] as MasterRow[],
    });
  }

  static getGroupOptions(params: {
    search?: string;
    classificationType?: ClassificationType | "";
    isActive?: boolean;
  }) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.GROUP_OPTIONS,
      { params, fallbackData: [] as MasterOption[] }
    );
  }

  static createGroup(body: {
    name: string;
    classificationType: ClassificationType;
    isActive?: boolean;
  }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.GROUP_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateGroup(
    id: string,
    body: { classificationType?: ClassificationType; isActive?: boolean }
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.GROUP_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  // =========================================================================
  // Three-level category mapping (hierarchy)
  // =========================================================================

  static getHierarchies(params: {
    page?: number;
    pageSize?: number;
    classificationType?: ClassificationType | "";
    primaryCategoryId?: string;
    subcategoryId?: string;
    groupId?: string;
    isActive?: boolean;
  }) {
    return this.request<HierarchyRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_LIST,
      { params, fallbackData: [] as HierarchyRow[] }
    );
  }

  static getHierarchyOptions(params: {
    classificationType?: ClassificationType | "";
    primaryCategoryId?: string;
    subcategoryId?: string;
    groupId?: string;
  }) {
    return this.request<HierarchyRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_OPTIONS,
      { params, fallbackData: [] as HierarchyRow[] }
    );
  }

  static createHierarchy(body: {
    classificationType: ClassificationType;
    primaryCategoryId: string;
    subcategoryId: string;
    groupId: string;
  }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_CREATE, {
      method: "POST",
      body,
    });
  }

  static setHierarchyActive(id: string, isActive: boolean) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_SET_ACTIVE.replace(":id", id),
      { method: "PATCH", body: { isActive } }
    );
  }

  /** Second cascade level: subcategories reachable from one primary category. */
  static getSubcategoriesForPrimaryCategory(primaryCategoryId: string) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_SUBCATEGORIES,
      { params: { primaryCategoryId }, fallbackData: [] as MasterOption[] }
    );
  }

  /** Third cascade level: groups reachable from one primary + subcategory pair. */
  static getGroupsForPrimaryAndSubcategory(
    primaryCategoryId: string,
    subcategoryId: string
  ) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.HIERARCHY_GROUPS,
      {
        params: { primaryCategoryId, subcategoryId },
        fallbackData: [] as MasterOption[],
      }
    );
  }

  // =========================================================================
  // Domains, regulatory domains and their rules
  // =========================================================================

  static getDomains(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_LIST, {
      params,
      fallbackData: [] as MasterRow[],
    });
  }

  static getDomainOptions(params: { search?: string; isActive?: boolean } = {}) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_OPTIONS,
      { params, fallbackData: [] as MasterOption[] }
    );
  }

  static createDomain(body: { name: string; isActive?: boolean }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateDomain(id: string, body: { isActive?: boolean }) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getRegulatoryDomains(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.REGULATORY_DOMAIN_LIST,
      { params, fallbackData: [] as MasterRow[] }
    );
  }

  static getRegulatoryDomainOptions(
    params: { search?: string; isActive?: boolean } = {}
  ) {
    return this.request<MasterOption[]>(
      ENDPOINTS.PRODUCT_CATEGORY.REGULATORY_DOMAIN_OPTIONS,
      { params, fallbackData: [] as MasterOption[] }
    );
  }

  static createRegulatoryDomain(body: { name: string; isActive?: boolean }) {
    return this.request(ENDPOINTS.PRODUCT_CATEGORY.REGULATORY_DOMAIN_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateRegulatoryDomain(id: string, body: { isActive?: boolean }) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.REGULATORY_DOMAIN_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getPrimaryCategoryDomainRules(params: {
    page?: number;
    pageSize?: number;
    primaryCategoryId?: string;
    domainId?: string;
    regulatoryDomainId?: string;
  }) {
    return this.request<DomainRuleRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_PRIMARY_LIST,
      { params, fallbackData: [] as DomainRuleRow[] }
    );
  }

  static upsertPrimaryCategoryDomainRule(body: {
    primaryCategoryId: string;
    domainId: string;
    regulatoryDomainId: string;
    isAllowed?: boolean;
  }) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_PRIMARY_UPSERT,
      { method: "POST", body }
    );
  }

  static deletePrimaryCategoryDomainRule(id: string) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_PRIMARY_DELETE.replace(":id", id),
      { method: "DELETE" }
    );
  }

  static getSubcategoryDomainRules(params: {
    page?: number;
    pageSize?: number;
    primaryCategoryId?: string;
    subcategoryId?: string;
    domainId?: string;
    regulatoryDomainId?: string;
  }) {
    return this.request<DomainRuleRow[]>(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_SUBCATEGORY_LIST,
      { params, fallbackData: [] as DomainRuleRow[] }
    );
  }

  static upsertSubcategoryDomainRule(body: {
    primaryCategoryId: string;
    subcategoryId: string;
    domainId: string;
    regulatoryDomainId: string;
    isAllowed?: boolean;
  }) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_SUBCATEGORY_UPSERT,
      { method: "POST", body }
    );
  }

  static deleteSubcategoryDomainRule(id: string) {
    return this.request(
      ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_SUBCATEGORY_DELETE.replace(
        ":id",
        id
      ),
      { method: "DELETE" }
    );
  }

  /** Most-specific-wins check for one category + domain + regulatory domain. */
  static resolveDomainRule(params: {
    primaryCategoryId: string;
    subcategoryId?: string;
    domainId: string;
    regulatoryDomainId: string;
  }) {
    return this.request<{
      isAllowed: boolean;
      matchedLevel: "SUBCATEGORY" | "PRIMARY" | "NONE";
      matchedRuleId: string | null;
      reason: string;
    }>(ENDPOINTS.PRODUCT_CATEGORY.DOMAIN_RULE_RESOLVE, { params });
  }

  // =========================================================================
  // Salts, drugs, schedules and salt-drug mapping
  // =========================================================================

  static getSalts(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(ENDPOINTS.MEDICINE_SALT.SALT_LIST, {
      params,
      fallbackData: [] as MasterRow[],
    });
  }

  static getSaltOptions(params: { search?: string; isActive?: boolean } = {}) {
    return this.request<MasterOption[]>(ENDPOINTS.MEDICINE_SALT.SALT_OPTIONS, {
      params,
      fallbackData: [] as MasterOption[],
    });
  }

  static createSaltMaster(body: { name: string; isActive?: boolean }) {
    return this.request(ENDPOINTS.MEDICINE_SALT.SALT_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateSaltMaster(
    id: string,
    body: { name?: string; isActive?: boolean }
  ) {
    return this.request(
      ENDPOINTS.MEDICINE_SALT.SALT_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getDrugs(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(ENDPOINTS.MEDICINE_SALT.DRUG_LIST, {
      params,
      fallbackData: [] as MasterRow[],
    });
  }

  static getDrugOptions(params: { search?: string; isActive?: boolean } = {}) {
    return this.request<MasterOption[]>(ENDPOINTS.MEDICINE_SALT.DRUG_OPTIONS, {
      params,
      fallbackData: [] as MasterOption[],
    });
  }

  static createDrug(body: { name: string; isActive?: boolean }) {
    return this.request(ENDPOINTS.MEDICINE_SALT.DRUG_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateDrug(id: string, body: { name?: string; isActive?: boolean }) {
    return this.request(
      ENDPOINTS.MEDICINE_SALT.DRUG_UPDATE.replace(":id", id),
      { method: "PUT", body }
    );
  }

  static getSchedules(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<ScheduleRow[]>(ENDPOINTS.MEDICINE_SALT.SCHEDULE_LIST, {
      params,
      fallbackData: [] as ScheduleRow[],
    });
  }

  static getScheduleOptions(
    params: { search?: string; isActive?: boolean } = {}
  ) {
    return this.request<{ id: number; name: string }[]>(
      ENDPOINTS.MEDICINE_SALT.SCHEDULE_OPTIONS,
      { params, fallbackData: [] as { id: number; name: string }[] }
    );
  }

  static createSchedule(body: {
    name: string;
    description?: string | null;
    isActive?: boolean;
  }) {
    return this.request(ENDPOINTS.MEDICINE_SALT.SCHEDULE_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateSchedule(
    id: string | number,
    body: { name?: string; description?: string | null; isActive?: boolean }
  ) {
    return this.request(
      ENDPOINTS.MEDICINE_SALT.SCHEDULE_UPDATE.replace(":id", String(id)),
      { method: "PUT", body }
    );
  }

  static getSaltDrugMappings(params: {
    page?: number;
    pageSize?: number;
    saltId?: string;
    drugId?: string;
    saltName?: string;
    drugName?: string;
  }) {
    return this.request<SaltDrugMappingRow[]>(
      ENDPOINTS.MEDICINE_SALT.MAPPING_LIST,
      { params, fallbackData: [] as SaltDrugMappingRow[] }
    );
  }

  static createSaltDrugMapping(body: { saltId: string; drugId: string }) {
    return this.request(ENDPOINTS.MEDICINE_SALT.MAPPING_CREATE, {
      method: "POST",
      body,
    });
  }

  // =========================================================================
  // Product forms
  // =========================================================================

  static getFormGroups(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
  }) {
    return this.request<MasterRow[]>(ENDPOINTS.PRODUCT_FORM.GROUP_LIST, {
      params,
      fallbackData: [] as MasterRow[],
    });
  }

  static getFormGroupOptions(
    params: { search?: string; isActive?: boolean } = {}
  ) {
    return this.request<MasterOption[]>(ENDPOINTS.PRODUCT_FORM.GROUP_OPTIONS, {
      params,
      fallbackData: [] as MasterOption[],
    });
  }

  static createFormGroup(body: { name: string; isActive?: boolean }) {
    return this.request(ENDPOINTS.PRODUCT_FORM.GROUP_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateFormGroup(
    id: string,
    body: { name?: string; isActive?: boolean }
  ) {
    return this.request(ENDPOINTS.PRODUCT_FORM.GROUP_UPDATE.replace(":id", id), {
      method: "PUT",
      body,
    });
  }

  static getForms(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    formGroupId?: string;
    isActive?: boolean;
  }) {
    return this.request<ProductFormRow[]>(ENDPOINTS.PRODUCT_FORM.FORM_LIST, {
      params,
      fallbackData: [] as ProductFormRow[],
    });
  }

  static getFormOptions(params: {
    search?: string;
    formGroupId?: string;
    isActive?: boolean;
  } = {}) {
    return this.request<
      { id: string; name: string; formGroupId: string; formGroupName: string | null }[]
    >(ENDPOINTS.PRODUCT_FORM.FORM_OPTIONS, { params, fallbackData: [] });
  }

  static createForm(body: {
    name: string;
    formGroupId: string;
    isActive?: boolean;
  }) {
    return this.request(ENDPOINTS.PRODUCT_FORM.FORM_CREATE, {
      method: "POST",
      body,
    });
  }

  static updateForm(
    id: string,
    body: { name?: string; formGroupId?: string; isActive?: boolean }
  ) {
    return this.request(ENDPOINTS.PRODUCT_FORM.FORM_UPDATE.replace(":id", id), {
      method: "PUT",
      body,
    });
  }

  // =========================================================================
  // V3 products
  // =========================================================================

  static getAdminProductListing(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    isPublished?: boolean;
    isRxRequired?: boolean;
    scheduleMedicineId?: number;
    marketerId?: string;
    marketerName?: string;
  }) {
    return this.request<AdminProductRow[]>(ENDPOINTS.PRODUCT_V3.ADMIN_LISTING, {
      params,
      fallbackData: [] as AdminProductRow[],
    });
  }

  static getProductDetails(productId: string) {
    return this.request<any>(
      ENDPOINTS.PRODUCT_V3.DETAILS.replace(":productId", productId),
      { fallbackData: null }
    );
  }

  static createProduct(body: Record<string, unknown>) {
    return this.request<any>(ENDPOINTS.PRODUCT_V3.CREATE, {
      method: "POST",
      body,
    });
  }

  static updateProduct(productId: string, body: Record<string, unknown>) {
    return this.request<any>(
      ENDPOINTS.PRODUCT_V3.UPDATE.replace(":productId", productId),
      { method: "PATCH", body }
    );
  }

  static setProductActive(
    productId: string,
    isActive: boolean,
    updationRemarks?: string
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_V3.SET_ACTIVE.replace(":productId", productId),
      { method: "PATCH", body: { isActive, updationRemarks } }
    );
  }

  static setProductStatus(
    productId: string,
    status: "Live" | "UnClassified",
    updationRemarks?: string
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_V3.SET_STATUS.replace(":productId", productId),
      { method: "PATCH", body: { status, updationRemarks } }
    );
  }

  /** Composition is a set, so this replaces every salt row for the product. */
  static replaceComposition(
    productId: string,
    salts: Record<string, unknown>[],
    updationRemarks?: string
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_V3.REPLACE_COMPOSITION.replace(":productId", productId),
      { method: "PUT", body: { salts, updationRemarks } }
    );
  }

  /** Mappings are set-valued too: send the full list, `[]` to unmap. */
  static replaceCategoryMappings(
    productId: string,
    mappings: { hierarchyId: string; isPrimary?: boolean }[],
    updationRemarks?: string
  ) {
    return this.request(
      ENDPOINTS.PRODUCT_V3.REPLACE_CATEGORY_MAPPINGS.replace(
        ":productId",
        productId
      ),
      { method: "PUT", body: { mappings, updationRemarks } }
    );
  }

  static getSubstitutes(
    productId: string,
    params: { matchStrength?: boolean; limit?: number } = {}
  ) {
    return this.request<any[]>(
      ENDPOINTS.PRODUCT_V3.SUBSTITUTES.replace(":productId", productId),
      { params, fallbackData: [] }
    );
  }

  static getVerificationHistory(productId: string, limit = 50) {
    return this.request<any[]>(
      ENDPOINTS.PRODUCT_V3.VERIFICATION_HISTORY.replace(":productId", productId),
      { params: { limit }, fallbackData: [] }
    );
  }

  /**
   * Asks the AI agent to work out the full catalogue record from a few known
   * details. Nothing is written: every master reference comes back either
   * resolved to a real id or flagged `isNew` for the caller to create first.
   *
   * It runs several model round trips, so it is slow by the standards of the
   * rest of this file - allow the better part of a minute.
   */
  static enrichProduct(body: {
    productName: string;
    brandName?: string;
    marketerName?: string;
    classificationType?: ClassificationType;
    additionalContext?: string;
  }) {
    return this.request<EnrichedProduct | null>(ENDPOINTS.PRODUCT_V3.ENRICH, {
      method: "POST",
      body,
      fallbackData: null,
      errorMessage: "Could not reach the enrichment service",
    });
  }
}
